import React from 'react';
import { useTranslation } from 'react-i18next';
import type { SingleValue } from 'react-select';

import type { IdAccountDto } from '@/services/ws-crm/src/main/resources/rest-api';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { Filter } from '@/js/libs/ui/src/V2';

import { useMarkedOptionLabel } from 'src/hooks/useMarkedOptionLabel';
import type { Option } from 'src/types';
import { useAccountsOptions } from '../hooks/useAccountsOptions';

interface AccountFilterProps {
  value: Option<IdAccountDto> | null;
  onChange: (value: SingleValue<Option<IdAccountDto>>) => void;
  label?: string;
  includeInactive?: boolean;
}

export const AccountFilter: React.FC<AccountFilterProps> = React.memo(function AccountFilter({ value, onChange, label, includeInactive }) {
  const [t] = useTranslation(['backend', 'common']);
  const formatOptionLabel = useMarkedOptionLabel({ t });
  const { options, pending: loading, total, searchFilter, setSearchFilter } = useAccountsOptions(!!includeInactive);

  const handleChange = useCallbackRef((newValue: SingleValue<Option<IdAccountDto>>) => {
    onChange(newValue);
  });

  return (
    <Filter<IdAccountDto>
      label={label || t('common:ACCOUNT')}
      loading={loading}
      selected={value}
      onChange={handleChange}
      serverSideFiltering={{
        filter: searchFilter,
        setFilter: setSearchFilter,
        total,
      }}
      options={options}
      formatOptionLabel={formatOptionLabel}
      dataTestId='account-dropdown'
    />
  );
});
